import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Helmet } from 'react-helmet-async';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowDownLeft, ArrowUpRight, CheckCircle, Clock, Loader, XCircle } from 'lucide-react';

const Transactions = () => {
  const { token } = useAuth(); // Get the token from AuthContext
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const getStatusProps = (status) => {
    switch (status.toLowerCase()) {
      case 'paid':
      case 'completed':
        return { text: 'Completed', color: 'text-green-400', bgColor: 'bg-green-500/10', icon: <CheckCircle size={14} /> };
      case 'rejected':
        return { text: 'Rejected', color: 'text-red-400', bgColor: 'bg-red-500/10', icon: <XCircle size={14} /> };
      case 'to be reviewed':
      case 'pending':
      default:
        return { text: 'Pending', color: 'text-yellow-400', bgColor: 'bg-yellow-500/10', icon: <Clock size={14} /> };
    }
  };

  useEffect(() => {
    const fetchTransactions = async () => {
      try {
        if (!token) {
          setError('No authentication token found.');
          setLoading(false);
          return;
        }
        const config = {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        };

        // Fetch both deposit and withdrawal history
        const [depositsRes, withdrawalsRes] = await Promise.all([
          axios.get('/api/deposits/my-history', config),
          axios.get('/api/withdrawals/my-history', config),
        ]);

        // Combine the results and explicitly add a type field
        const deposits = depositsRes.data.map(deposit => ({ ...deposit, type: 'deposit' }));
        const withdrawals = withdrawalsRes.data.map(withdrawal => ({ ...withdrawal, type: 'withdrawal' }));

        const combinedTransactions = [...deposits, ...withdrawals];

        // Sort transactions by date (most recent first)
        combinedTransactions.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

        setTransactions(combinedTransactions);
        console.log('Fetched combined transactions:', combinedTransactions);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching transactions:', err);
        setError('Error loading transactions.');
        setLoading(false);
        if (axios.isAxiosError(err) && err.response && err.response.status === 401) {
          setError('Authentication token expired. Please re-authenticate.');
          // Optionally redirect to login
        }
      }
    };

    fetchTransactions();
  }, [token]); // Refetch if token changes

  return (
    <>
    <Helmet>
      <title>Transactions | RatingApp</title>
      <meta name="description" content="View your complete transaction history on RatingApp including deposits, withdrawals, and rating earnings." />
    </Helmet>
    <div className="w-full pb-28 from-gray-900 via-blue-900/30 to-gray-900 text-white font-sans">
      <div className="max-w-2xl mx-auto px-4 py-8">
        <motion.h1
          className="text-4xl font-extrabold tracking-tighter bg-clip-text text-transparent bg-gradient-to-r from-cyan-400 to-blue-500 text-center mb-2"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          Transactions
        </motion.h1>
        <p className="text-slate-400 text-center mb-8">A complete history of your deposits and withdrawals.</p>

        {loading && (
          <div className="text-center py-12 flex items-center justify-center gap-2 text-slate-400">
            <Loader className="animate-spin" />
            <span>Loading Transactions...</span>
          </div>
        )}
        {error && <p className="text-red-400 text-center py-12">{error}</p>}

        <AnimatePresence>
          {!loading && !error && (
            <motion.ul 
              className="list-none p-0 m-0 space-y-3"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              {transactions.length === 0 ? (
                <li className="text-slate-400 text-center py-12">No transactions found.</li>
              ) : (
                transactions.map((transaction, index) => {
                  const isDeposit = transaction.type === 'deposit';
                  const status = getStatusProps(transaction.status);
                  return (
                    <motion.li
                      key={transaction._id}
                      className="bg-gray-800/20 backdrop-blur-xl border border-white/10 rounded-lg p-4"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isDeposit ? 'bg-green-500/10' : 'bg-red-500/10'}`}>
                            {isDeposit ? <ArrowDownLeft size={20} className="text-green-400" /> : <ArrowUpRight size={20} className="text-red-400" />}
                          </div>
                          <div>
                            <h5 className="text-white font-semibold">{isDeposit ? 'USDT Deposit' : 'USDT Withdrawal'}</h5>
                            <p className="text-slate-400 text-xs mt-1">
                              {new Date(transaction.createdAt).toLocaleString()}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <h6 className={`font-bold ${isDeposit ? 'text-green-400' : 'text-red-400'}`}>
                            {isDeposit ? '+' : '-'}{(transaction.amount || 0).toFixed(2)}
                          </h6>
                          <div className={`mt-1 flex items-center justify-end gap-1.5 text-xs font-medium px-2 py-0.5 rounded-full ${status.bgColor} ${status.color}`}>
                            {status.icon}
                            <span>{status.text}</span>
                          </div>
                        </div>
                      </div>
                    </motion.li>
                  );
                })
              )}
            </motion.ul>
          )}
        </AnimatePresence>
      </div>
    </div>
    </>
  );
};

export default Transactions;
